import { useContext, useState } from 'react';

import { MDText } from 'i18n-react';

import { Button, PayCardIcon } from '@motorway/mw-highway-code';
import { InfoBox, Radio } from '@motorway/mw-highway-code/alpha';
import { INFOBOX_VARIANT, SIZE, VARIANT } from '@motorway/mw-highway-code/enums';

import { VehicleLookupContext } from 'Context/vehicleLookup';

import { EVENT } from 'Utilities/analytics/optimizely/constants';
import { useOptimizelyTrack } from 'Utilities/analytics/optimizely/useOptimizelyTrack';
import { type HeroAnimationState, HERO_ANIMATION_STATES } from 'Utilities/animation';

import { setFinanceFlag } from '../FinanceExperiment.helpers';
import LocalTexts from '../HeroFinance.texts.json';

import styles from '../HeroFinance/HeroFinance.scss';

const LocalT = new MDText(LocalTexts);

type HeroFinanceQuestionProps = {
  loadingHandler: (state: HeroAnimationState) => void;
  onFinanceSubmit: (isOnFinance: boolean) => void;
};

const RADIO_OPTIONS = [
  { label: LocalT.translate('onFinance'), value: true },
  { label: LocalT.translate('noFinance'), value: false },
] as const;

export const HeroFinanceQuestion = ({
  loadingHandler,
  onFinanceSubmit,
}: HeroFinanceQuestionProps) => {
  const [hasError, setHasError] = useState<boolean>(false);
  const [isOnFinance, setIsOnFinance] = useState<boolean | null>(null);
  const { OPTIMIZELY_TRACK } = useOptimizelyTrack();
  const { vehicleLookupState: vehicle } = useContext(VehicleLookupContext);

  const handleRadioChange = (value: boolean) => {
    setIsOnFinance(value);
    setHasError(false);
    OPTIMIZELY_TRACK(EVENT.MW_5_3_HERO_FINANCE_RADIO_SELECTED, {}, { isOnFinanceBoolean: value });
  };

  const handleContinue = () => {
    if (isOnFinance === null) {
      setHasError(true);
      return;
    }

    OPTIMIZELY_TRACK(EVENT.MW_5_3_HERO_FINANCE_CONTINUE_CLICKED, {}, { isOnFinanceBoolean: isOnFinance });
    onFinanceSubmit(isOnFinance);

    const key = vehicle?.vrm;
    if (key) {
      setFinanceFlag(key, isOnFinance);
    }

    loadingHandler(HERO_ANIMATION_STATES.mileage);
  };

  const infoBoxKey = isOnFinance ? 'withFinance' : 'noFinance';

  const showInfoBox = isOnFinance !== null;

  return (
    <>
      <div className={styles.radioGroup}>
        {RADIO_OPTIONS.map(({ label, value }) => (
          <Radio
            key={String(label)}
            fullWidth
            formProps={{
              input: {
                checked: isOnFinance === value,
                name: 'heroFinance',
                onChange: () => handleRadioChange(value),
                value: String(value),
              },
            }}
            id={`radio-heroFinance-${value}`}
            label={label}
            variant='card'
          />
        ))}
      </div>

      {isOnFinance !== null && (
        <InfoBox
          content={LocalT.translate(`infoBox.${infoBoxKey}.content`)}
          heading={LocalT.translate(`infoBox.${infoBoxKey}.title`)}
          variant={INFOBOX_VARIANT.INFO}
        />
      )}

      {!showInfoBox && (
        <div className={styles.financeMessageWrapper}>
          <span className={styles.iconWrapper}>
            <PayCardIcon size={SIZE.SMALL} />
          </span>
          <p data-thc-style-override className={styles.financeMessage}>
            {LocalT.translate('outstandingFinance', { boldText: <span>{LocalT.translate('outstandingFinanceBold')}</span> })}
          </p>
        </div>)
      }

      {hasError && <InfoBox
        content=''
        heading={LocalT.translate('infoBox.error')}
        variant={INFOBOX_VARIANT.ERROR}
      />}

      <Button
        data-thc-button
        icon={'chevron'}
        label="Continue"
        onClick={handleContinue}
        size={SIZE.LARGE}
        variant={VARIANT.PRIMARY}
      />
    </>
  );
};
